import React from 'react'
import { Dialog } from '@mui/material'
import { Close as CloseIcon, ContentCopy as CopyIcon } from '@mui/icons-material'
import { useDispatch } from '../../store'
import { postWorkflow } from '../../store/package/actionCreator'
import { Workflow } from '../../store/package/reducer'
import l from '../../page/SubscribersPageV2.module.css'
import s from './DuplicatePackageDialog.module.css'

/*
 * "Duplicate workflow" — a copy with only the title and the price to set;
 * everything else (type, subtitle, sources or quotas, input hint, button,
 * colour, fake-check / AI-summary prices, report country) is the original's.
 * The body is the one PopupWorkflowV2 sends for the same workflow, and the
 * title / price follow its rules: Latin letters and the classic form's
 * punctuation, price 0.01 – 99 (WebInt and add-quota workflows have none).
 * The backend refuses a title already in use, so that is checked first.
 */

// the classic form's latinOnly (function/validation.ts)
const LATIN_ONLY = /^[a-zA-Z0-9.,_\-!:$€"()+/% ]*$/
const WEBINT_PARAMS = ['quotaprofiler-lookup', 'quotaprofiler-crawler', 'quotaprofiler-cases', 'quotaprofiler-periodic', 'quotaprofiler-monitoring',
  'quotawebint-lookup', 'quotawebint-crawler', 'quotawebint-cases', 'quotawebint-periodic', 'quotawebint-monitoring']

export function DuplicateWorkflowDialog({ workflow, title: initialTitle, takenTitles, theme, onClose, onCreated }: {
  workflow: Workflow
  /** the suggested title — already unique ("… (copy)", "… (copy 2)") */
  title: string
  takenTitles: string[]
  theme: 'light' | 'dark'
  onClose: () => void
  onCreated: () => void
}) {
  const dispatch = useDispatch()
  const type = String(workflow.type)
  const isQuota = type === 'webint' || type === 'additional_quotas'
  const [title, setTitle] = React.useState(initialTitle)
  const [price, setPrice] = React.useState(String(workflow.price ?? 1))
  const [errors, setErrors] = React.useState<{ title?: string; price?: string }>({})
  const [saving, setSaving] = React.useState(false)

  const save = () => {
    const e: { title?: string; price?: string } = {}
    const t = title.trim()
    if (!t) e.title = 'Enter a title'
    else if (!LATIN_ONLY.test(t)) e.title = 'Latin letters, digits and . , _ - ! : $ € " ( ) + / % only'
    else if (takenTitles.some((x) => x.trim().toLowerCase() === t.toLowerCase())) e.title = 'A workflow with this title already exists'
    const p = Number(price)
    if (!isQuota && (!Number.isFinite(p) || p < 0.01 || p > 99)) e.price = 'From 0.01 to 99'
    setErrors(e)
    if (Object.keys(e).length) return

    const w = workflow as any
    let parsed: any = null
    try { parsed = JSON.parse(w.input || '') } catch { parsed = null }
    // ipgeo_number keeps {phone, ip} in `input`; the form sends them as input / inputIP
    const isGeoPhone = type === 'ipgeo_number' && parsed && typeof parsed === 'object'
    const body = {
      type,
      price: type === 'additional_quotas' ? 0 : isQuota ? w.price : p,
      sources: isQuota ? WEBINT_PARAMS : (w.sources || []),
      fakeCheckPrice: w.fakeCheckPrice ?? 0,
      reportCountry: type === 'profiler' ? (parsed?.reportCountry ?? null) : null,
      aiSummarizingPrice: w.aiSummarizingPrice ?? 0,
      maxNum: w.maxNum ?? 0,
      color: w.color,
      title: t,
      subTitle: (w.subTitle || '').trim(),
      input: isGeoPhone ? (parsed.phone || '') : (w.input || ''),
      inputIP: isGeoPhone ? (parsed.ip || '') : (w.input || ''),
      button: (w.button || '').trim(),
    }
    setSaving(true)
    // the callback runs only when the server accepted it; an error shows as a snackbar and the window stays
    dispatch(postWorkflow(body, () => { onCreated(); onClose() }))
    setTimeout(() => setSaving(false), 1500)
  }

  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2 } }}>
      <div className={`${l.theme} ${s.shell}`} data-theme={theme}>
        <div className={s.top}>
          <span className={s.icon}><CopyIcon sx={{ fontSize: 18 }} /></span>
          <div className={s.topText}>
            <b>Duplicate workflow</b>
            <span>Everything else is copied from “{workflow.title}”.</span>
          </div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close"><CloseIcon sx={{ fontSize: 18 }} /></button>
        </div>

        <div className={s.body}>
          <label className={s.field}>
            <span className={s.label}>Title</span>
            <input
              className={`${s.input} ${errors.title ? s.inputErr : ''}`}
              value={title}
              autoFocus
              onChange={(e) => { setTitle(e.target.value); setErrors((er) => ({ ...er, title: undefined })) }}
              onKeyDown={(e) => { if (e.key === 'Enter') save() }}
            />
            {errors.title ? <span className={s.err}>{errors.title}</span> : <span className={s.hint}>Must differ from every other workflow</span>}
          </label>
          {!isQuota && (
            <label className={s.field}>
              <span className={s.label}>Price, €</span>
              <input
                className={`${s.input} ${s.mono} ${errors.price ? s.inputErr : ''}`}
                type="number"
                min={0.01}
                max={99}
                step="0.01"
                value={price}
                onChange={(e) => { setPrice(e.target.value); setErrors((er) => ({ ...er, price: undefined })) }}
                onKeyDown={(e) => { if (e.key === 'Enter') save() }}
              />
              {errors.price ? <span className={s.err}>{errors.price}</span> : <span className={s.hint}>Original: €{workflow.price}</span>}
            </label>
          )}
        </div>

        <div className={s.foot}>
          <button type="button" className={s.ghost} onClick={onClose}>Cancel</button>
          <button type="button" className={s.primary} onClick={save} disabled={saving}>{saving ? 'Creating…' : 'Create copy'}</button>
        </div>
      </div>
    </Dialog>
  )
}
